import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Storage, Upload, AutoAwesome } from "@mui/icons-material";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import { Button } from "common/components";
import HeaderNew from "../../../../../common/components/custom/header-new/HeaderNew";
import HubHeader from "../../../../../common/components/custom/hub-header/HubHeader";
import { ToastMessageUtil } from "../../../../../common/components/base/toast-message/ToastMessage.util";
import { ToastVariant } from "../../../../../common/enums/controls/toast.enum";
import { MainRoutes } from "../../../../../common/config";
import {
  TagDefinition,
  TagLibraryConfig,
  TagLibraryByIdResponse
} from "../../../data/models/configuration-hub.model";
import { ConfigurationHubService } from "../../../data/services/configuration-hub.service";
import "../create-tag-library-config/CreateTagLibraryConfig.scss";
import { Loading } from "modules/_shared";

interface ConfigurationData {
  configName: string;
  categoryName: string;
  categoryDescription: string;
  tags: TagDefinition[];
  masterObjectId?: number; // Add masterObjectId to the interface
}

const CompleteTagLibrary: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get configuration data from location state or localStorage
  const [configData, setConfigData] = useState<ConfigurationData>({
    configName: "",
    categoryName: "",
    categoryDescription: "",
    tags: [],
    masterObjectId: undefined
  });

  // Add state for API response data
  const [apiData, setApiData] = useState<TagLibraryByIdResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let masterObjectId: number | undefined;

    // Try to get data from location state first (passed from previous step)
    if (location.state) {
      const {
        configName,
        categoryName,
        categoryDescription,
        tags,
        masterObjectId: objId,
        cfihosMasterObjectId
      } = location.state;
      const data = {
        configName: configName || "",
        categoryName: categoryName || "",
        categoryDescription: categoryDescription || "",
        tags: tags || [],
        masterObjectId: objId || cfihosMasterObjectId // Check both property names
      };
      setConfigData(data);
      masterObjectId = objId || cfihosMasterObjectId; // Use either property
    } else {
      // Fallback: get from localStorage (in case of direct navigation)
      try {
        const tempConfig = localStorage.getItem("tempTagLibraryConfig");
        if (tempConfig) {
          const data = JSON.parse(tempConfig);
          setConfigData(data);
          masterObjectId = data.masterObjectId;
        } else {
          // If no data is available, redirect back to create page
          navigate(MainRoutes.CREATE_TAG_LIBRARY_CONFIG);
          return;
        }
      } catch (parseError) {
        console.error("Error parsing temp config data:", parseError);
        navigate(MainRoutes.CREATE_TAG_LIBRARY_CONFIG);
        return;
      }
    }

    // Call API if masterObjectId is available
    if (masterObjectId) {
      fetchTagLibraryData(masterObjectId);
    }
  }, [location.state, navigate]);

  // Function to fetch tag library data from API
  const fetchTagLibraryData = async (id: number) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await ConfigurationHubService.getTagLibraryById(id);

      if (response.data) {
        setApiData(response.data);
      } else if (response.error) {
        setError("Failed to fetch tag library data");
        ToastMessageUtil.show(
          "Failed to fetch tag library data",
          ToastVariant.ERROR
        );
      } else {
        setError("No data received from API");
      }
    } catch (err) {
      console.error("Error fetching tag library data:", err);
      setError("An error occurred while fetching data");
      ToastMessageUtil.show(
        "An error occurred while fetching data",
        ToastVariant.ERROR
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveConfiguration = () => {
    // Use API data if available, otherwise fall back to configData
    const configName = apiData?.configurationName || configData.configName;
    const categoryName = apiData?.name || configData.categoryName;
    const categoryDescription =
      apiData?.description || configData.categoryDescription;
    const tagsProcessed = apiData?.totalTagsProcessed || configData.tags.length;

    const newConfig: TagLibraryConfig = {
      id: apiData?.id || Date.now(),
      guidCode: apiData?.guidCode || `guid-${Date.now()}`,
      name: categoryName,
      description: categoryDescription || "",
      cfiHOSId: apiData?.cfiHOSId || `CFI-${Date.now()}`,
      isCFIHOS: apiData?.isCFIHOS || false,
      order: apiData?.order || 1,
      isActive: apiData?.isActive ?? true,
      configurationName: configName,
      totalTagsProcessed: tagsProcessed,
      createdBy: apiData?.createdBy || "Current User",
      createdByName: "Current User",
      createdDate: apiData?.createdDate || new Date().toISOString(),
      updatedBy: apiData?.updatedBy || "Current User",
      updatedDate: apiData?.updatedDate || new Date().toISOString()
    };

    // Save to localStorage for demo purposes
    try {
      const saved = localStorage.getItem("tagLibraryConfigurations");
      const configs = saved ? JSON.parse(saved) : [];
      configs.push(newConfig);
      localStorage.setItem("tagLibraryConfigurations", JSON.stringify(configs));

      // Clean up temporary data
      localStorage.removeItem("tempTagLibraryConfig");

      ToastMessageUtil.show(
        "Tag library configuration created successfully",
        ToastVariant.SUCCESS
      );
      navigate(MainRoutes.TAG_LIBRARY_CONFIG);
    } catch (saveError) {
      ToastMessageUtil.show("Failed to save configuration", ToastVariant.ERROR);
    }
  };

  const handleBackToList = () => {
    // Clean up temporary data
    localStorage.removeItem("tempTagLibraryConfig");
    navigate(MainRoutes.TAG_LIBRARY_CONFIG);
  };

  const renderProgressTracker = () => {
    const phases = [
      { key: "setup", label: "Setup", icon: <Upload /> },
      { key: "enrich", label: "Enrich", icon: <AutoAwesome /> },
      { key: "complete", label: "Complete", icon: <DoneOutlinedIcon /> }
    ];

    const currentPhase = "complete";
    const getPhaseIndex = (phase: string) =>
      phases.findIndex((p) => p.key === phase);
    const currentIndex = getPhaseIndex(currentPhase);

    const isPhaseActive = (phaseKey: string) => currentPhase === phaseKey;
    const isPhaseCompleted = (phaseKey: string) => {
      const phaseIndex = getPhaseIndex(phaseKey);
      // Mark all previous phases as completed, and also the complete phase itself as completed
      return (
        phaseIndex < currentIndex ||
        (currentPhase === "complete" && phaseKey === "complete")
      );
    };

    return (
      <div className="progress-tracker">
        <div className="progress-steps">
          {phases.map((phase, index) => (
            <div
              key={phase.key}
              className={`progress-step ${
                isPhaseCompleted(phase.key) ? "completed" : ""
              }`}
            >
              <div
                className={`step-icon ${
                  isPhaseCompleted(phase.key)
                    ? "completed"
                    : isPhaseActive(phase.key)
                    ? "active"
                    : "inactive"
                }`}
              >
                {phase.icon}
              </div>
              <div className="step-label">{phase.label}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <>
        <HeaderNew showSearch={false} />
        <div className="create-tag-library-container">
          <HubHeader
            className="header"
            title="Tag Library Configuration Complete"
            description="Review and save your tag library configuration"
            // showBackButton={true}
            // backRoute={MainRoutes.TAG_ENRICHMENT_PHASE}
          />
          <div className="container">
            {renderProgressTracker()}
            <div className="loading-container">
              <Loading />
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <HeaderNew showSearch={false} />
      <div className="create-tag-library-container">
        <HubHeader
          className="header"
          title="Tag Library Configuration Complete"
          description="Review and save your tag library configuration"
          // showBackButton={true}
          // backRoute={MainRoutes.TAG_ENRICHMENT_PHASE}
        />
        <div className="container">
          {renderProgressTracker()}
          <div className="config-card">
            <div className="completion-section">
              {/* <div className="completion-icon">
                <Storage />
              </div> */}
              <h2 style={{ color: "black", marginBottom: "16px" }}>
                Tag Library Configuration Completed
              </h2>
              <p className="card-subtitle" style={{ color: "black" }}>
                Your tag library has been successfully created and is ready to
                use in flow configurations.
              </p>

              <div className="completion-summary">
                <div className="summary-row">
                  <span className="summary-label">Configuration Name:</span>
                  <span className="summary-value">
                    {apiData?.configurationName ||
                      configData.configName ||
                      "Loading..."}
                  </span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Tag Category Name:</span>
                  <span className="summary-value">
                    {apiData?.name || configData.categoryName || "Loading..."}
                  </span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Tags Processed:</span>
                  <span className="summary-value">
                    {apiData?.totalTagsProcessed !== undefined
                      ? `${apiData.totalTagsProcessed} tags`
                      : `${configData.tags.length} tags`}
                  </span>
                </div>
                {/* {isLoading && (
                  <div className="summary-row">
                    <span className="summary-label">Status:</span>
                    <span className="summary-value">
                      Loading configuration data...
                    </span>
                  </div>
                )} */}
                {error && (
                  <div className="summary-row">
                    <span className="summary-label">Error:</span>
                    <span className="summary-value" style={{ color: "red" }}>
                      {error}
                    </span>
                  </div>
                )}
              </div>

              <div className="completion-actions">
                {/* <Button variant="secondary" onClick={handleBackToList}>
                  Back to Tag Library
                </Button> */}
                <Button variant="primary" onClick={handleBackToList}>
                  Back to Tag Library
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CompleteTagLibrary;
